import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { Count } from "./Pill";

/**
 * Abas com o padrão ARIA completo (AGENTS §17): `tablist`/`tab`, seleção
 * manual, tabindex móvel e navegação por setas, Home e End.
 *
 * A seleção é manual de propósito: mover o foco entre abas NÃO troca a
 * superfície, porque cada troca aqui recarrega listas e filtros.
 */
export interface TabItem {
  id: string;
  label: string;
  count?: number;
  disabled?: boolean;
  icon?: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Nome acessível do conjunto, exigido quando não há título visível ao lado. */
  label: string;
  /** `id` do painel controlado, quando existe um só. */
  controls?: string;
  className?: string;
}

export function Tabs({ items, value, onChange, label, controls, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const focusAt = (index: number) => {
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>(
      "button[role='tab']:not([disabled])",
    );
    if (!buttons || buttons.length === 0) return;
    const bounded = (index + buttons.length) % buttons.length;
    buttons[bounded]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        "button[role='tab']:not([disabled])",
      ) ?? [],
    );
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (current < 0) return;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      focusAt(current + 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      focusAt(current - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAt(buttons.length - 1);
    }
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={className ? `cx-tabs ${className}` : "cx-tabs"}
      onKeyDown={handleKeyDown}
    >
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`cx-tab-${item.id}`}
            aria-selected={selected}
            aria-controls={controls}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
          >
            {item.icon && <span aria-hidden="true">{item.icon}</span>}
            {item.label}
            {item.count !== undefined && <Count value={item.count} />}
          </button>
        );
      })}
    </div>
  );
}
